import { Observable } from 'rxjs';
import { ValidatorFn, UntypedFormGroup } from '@angular/forms';
// eslint-disable-next-line
import { EmailQueueDto } from '@ups/xplat/api/dto';
import { IDynamicEmployeeOptions } from './dynamic-employee.interface';
import { IDynamicSublocationOptions } from './dynamic-sublocation.interface';
import { IDynamicTypeaheadOptions } from './dynamic-typeahead.interface';
import {
  DynamicModelType,
  DynamicPropertyModelType,
} from './dynamic-model-type';
export * from './dynamic-model-type';
/* eslint-disable */
// NOTE: we can reenable eslint once mix-casing on properties if 100% consistent between frontend and backend
export interface IDynamicModelOptions
  extends
    IDynamicSublocationOptions,
    IDynamicEmployeeOptions,
    IDynamicTypeaheadOptions {
  // add isActive in the filter, by default null
  allowToggleActive?: boolean;
  // company typeahead: show ONLY companies where IsActive === true (opt-in per usage, filtered client-side). Distinct from allowToggleActive (employee toggle).
  activeOnly?: boolean;
  // hide by default
  hidden?: boolean;
  // ignore any label above the control
  labelIgnore?: boolean;
  // display label inline next to control instead of default above it
  labelInline?: boolean;
  // use a soft style label (not bold and dominant)
  labelSoft?: boolean;
  // checkbox: disable the named formControlName when checked
  disableFormControlName?: string;
  // checkbox: show the hidden formControlName when checked
  hiddenFormControlName?: string;
  // checkbox: show the formControlName when unchecked - otherwise hide it
  uncheckedFormControlName?: string;
  // checkbox: explicitly check
  checked?: boolean;
  // tooltip (often used with web hover)
  tooltip?: string;
  // shows in (i) badge next to the label so user sees there is a tooltip and can hower the badge or the label to get the tooltip message
  showTooltipIcon?: boolean;
  // custom style attributes
  style?: {
    button?: Partial<CSSStyleDeclaration>;
    container?: Partial<CSSStyleDeclaration>;
    label?: Partial<CSSStyleDeclaration>;
  };
  // custom layout
  layout?: IDynamicControlLayout;
  // collection of custom validations used throughout various dynamic controls
  customValidations?: Array<DynamicCustomValidationTypes>;
  // max character length
  maxlength?: number;
  // textarea: visible rows before it scrolls (mobile min height, WI 34928); defaults to 4
  rows?: number;
  // number and numerictextbox: can set a decimal range for precision
  decimals?: number;
  // date: minimum date
  minDate?: Date;
  // date: maximum date. Compared on the local CALENDAR DAY, so a max of Nov 12 admits any time
  // on Nov 12. Pass a local midnight; a max carrying a time component still admits the whole day.
  maxDate?: Date;
  // date: show time picker
  showTimePicker?: boolean;
  // typeahead: api options
  api?: {
    host?: string;
    endpoint?: string;
    model?: IApiModelType;
    properties?: string; // comma separated properties (data returned from api)
  };

  alwaysApplyQueryFilter?: boolean;
  /** array of controls to update based on the value change. */
  updateOnValueChange?: Array<IDynamicUpdateOnValueChange>;
  /**
   * Opt-in conflict resolution for a control that is the *target* of one or more
   * `updateOnValueChange` pushes. When an auto-calculated default would overwrite a value
   * the user manually selected or that was loaded from a saved record, instead of silently
   * overwriting it we can keep it, keep the default, or prompt the user to choose.
   *
   * Default semantics (resolved in `resolveConflictResolution()`):
   * - key ABSENT  => behaves as 'silent-default' (today's always-overwrite; fully backward compatible).
   * - key PRESENT (even `{}`) => `mode` defaults to 'prompt', `required` to false, wording to `label`.
   */
  conflictResolution?: IDynamicConflictResolution;
  /**
   * Typeahead only. When true, attach `dynamicValidatorNoSearchError` so a thrown remote
   * search invalidates the form until a later search succeeds. **Default off / opt-in:**
   * do not enable for hazard or other critical reporting fields where blocking submit
   * would remove the only path for the worst-served crews when search is down. The
   * typeahead still sets `hasSearchError_dynamic` and emits `searchFailed` regardless.
   */
  blockSubmitOnSearchFailure?: boolean;
  /**
   * Typeahead only, WI 35277 lookup gate. When true, a failed saved-value lookup does not
   * invalidate the form (no `dynamicValidatorNoPrefillError`); the control still flags
   * `hasPrefillError_dynamic` so the host can warn. Use for a lookup whose outage must not
   * block saving (e.g. Salesforce). Has no effect unless the form opted into the gate.
   */
  lookupGateSoftFail?: boolean;
  /**
   * Internal: set on a `target` message emitted by `_setSelectedItem` so the target control can
   * report which source control (and its selected value) triggered an auto-default. Not for config use.
   */
  conflictSource?: {
    formControlName?: string;
    label?: string;
    valueDisplay?: string;
  };
  // typeahead: scope a typeahead query based on the value of another formControlName
  /**
   * If I have 2 dropdowns A and B then if I want to narrow the results on B according to what was selected in A then set the scopeQueryWhenFormControlIsSet on the B.
   * scopeQueryWhenFormControlIsSet: {
          formControlName: 'vpClientId', // this is the formControlName of A
          matchTargetProperty: 'ParentCustomerID', // this is the property of the selected value of A to match against
          queryParamName: 'parentCustomerIDs',
        },
   */
  scopeQueryWhenFormControlIsSet?: {
    formControlName?: string;
    matchTargetProperty?: string;
    queryParamName?: string;
  };
  /**
   * typeahead: initial query to make when initializing the control.
   * Use it to avoid data loss. Here is why:
   * Type-ahead dropdowns only load the first N results. If the currently selected value isn’t among them, no value is selected and the field becomes null when saving, causing data loss.
   * Initialize the dropdown (initWithQuery) using the item's name (or ID via the backend API) to ensure the correct value is included in the result set — though this may still fail if multiple items share the same name.
   */
  initWithQuery?: string;
  typeahead?: {
    matchTargetProperty?: string;
    /** When true the target typeahead control value is set to null if no matching value was found in searchQuery for the matchTargetProperty.
     * This is useful when using updateOnValueChange.calculatedPropertyValue property and there was no candidate found to select based on the calculatedPropertyValue.
     */
    setNullValueIfNotFoundInSearch?: boolean;
  };
  /** typeahead (job/employee): when true and exactly one result is a user favorite, pre-select it */
  preSelectSingleFavorite?: boolean;
  /**
   * Form-level provenance: true when the form is editing an existing record (not new/duplicate).
   * Set by createConfigGroup from dynamic-page `editing: !isNew`. Used to suppress
   * preSelectSingleFavorite on reopen — hasNoSelection alone cannot answer "was this ever saved".
   * ADO #30156.
   */
  editing?: boolean;
  /** typeahead (employee): favorite type key (e.g. 'projectManager', 'safetyContact') to load and toggle favorites per type */
  favoriteType?: string;
  // file: specific directory to use
  directory?: string;
  // model is a primary control (mostly used with buttons)
  primary?: boolean;
  // model is a submit control (mostly used with buttons)
  submit?: boolean;
  // show a cancel button alongside the submit/save button
  showCancelButton?: boolean;
  // close the modal when cancel is pressed
  closeOnCancel?: boolean;
  // prompt when canceling a dirty form
  confirmCancelOnDirty?: boolean;
  // custom confirmation message for cancel
  cancelConfirmMessage?: string;
  // show a delete draft button alongside the submit/save button
  showDeleteDraftButton?: boolean;
  // show a draft button alongside the submit/save button
  showDraftButton?: boolean;
  // accordion rollups of multiple models
  accordion?: Array<IDynamicModel>;
  accordionCloseOthers?: boolean;
  accordionHasError?: boolean;
  accordionOpen?: boolean;
  accordionRequiredValues?: string;
  accordionRequiredValuesApplyToAll?: boolean;
  accordionRequiredValueRequiresAnother?: string;
  // this is used with accordions right now to specify a min limit on what can be viewed as acceptable with filling out controls marked as required
  // not prefixed with 'accordion' as this option could be used with other setups
  minimumFieldsRequired?: number;
  // buttongroup
  buttongroup?: Array<IDynamicButtonGroup>;
  // buttongroup should ignore selected states
  buttonGroupIgnoreState?: boolean;
  // explicitly emit an eventBus dynamicDataUpdate event when change occurs on this control
  emitEventOnChange?: boolean;
  // show any formControlName when the control with this setting gets the listed values
  showOnValues?: {
    formControlName: string;
    dynamicControlName?: string;
    dynamicControlNames?: string[];
    values: Array<string | number | boolean>;
  };
  // indicates if the control was combined with another to modify behavior of found control
  valueMatchFound?: boolean;
  // allow multiple file attachments
  fileAllowMultiple?: boolean;
  // used when a control is used outside of dynamic groupings, will remove default group margins, etc.
  standalone?: boolean;
  // admin builder specific options
  admin?: {
    allowDelete?: boolean;
  };

  signature?: IDynamicSignaturePadOptions;

  isMultiSelect?: boolean;
  isReusable?: boolean;
  dynamicContainerId?: string;
  dynamicContainerResponseId?: string;
  isEditReview?: boolean;
  isReadOnly?: boolean;
  reviewId?: string;
  formPath?: string;
  deleteComment?: string;
  submittedBy?: string;
  submittedUserId?: string;
  pointToViewpoint?: boolean;
  /** typeahead-job (WI 34969): scope the search to the signed-in user's default company. */
  scopeToUserDefaultCompany?: boolean;
  pointToSparta?: boolean;
  valueQueryParamName?: string;
  shortName?: string;
  showClosedJobsCheckbox?: boolean;
  /** Disable default spacing for the control. */
  disableDefaultSpacing?: boolean;
  /**
   * Completely custom style classes for the control.
   */
  customStyleClasses?: string;
  apiTimeFormat?: string;
}

/**
 * Item returned by an API by DataResolver can be mapped to IApiModelType so it is searchable
 */
export interface IApiModelType {
  id: string | number;
  name: string | number;
  description?: string | number;
  active?: string | number;
  altId?: string | number;
}

/**
 * Items returned from API by dataResolver() are searchable if they are of this interface.
 * It they are not and you want to still make them searchable convert them using IDynamicModel.options.api.model
 */
export interface IApiModelSearchable extends Partial<IApiModelType> {
  Id?: string | number;
  Name?: string | number;
  Description?: string | number;
  Active?: string | number;
}

// Validations which are dynamic control type specific can prefix their name with the type
export type DynamicCustomValidationTypes =
  | 'date: less than current'
  | 'date: greater than current'
  | 'date: greater than past 7 days';
// eslint-disable-next-line @typescript-eslint/naming-convention
export const DynamicCustomValidations: Array<DynamicCustomValidationTypes> = [
  'date: less than current',
  'date: greater than current',
  'date: greater than past 7 days',
];

export type IDynamicInputType =
  'email' | 'number' | 'password' | 'search' | 'tel' | 'text' | 'url';
// various controls that never need to be tracked with FormGroups
// for example, auxiliary UI elements in the form structure
export const dynamicFormGroupExcludedControlNames = ['copyLinkButton'];

export interface IDynamicModel<T = unknown> {
  clientId?: string;
  formControlName?: string;
  formArrayName?: string;
  reportCategoryName?: string;
  label?: string;
  instruction?: string;
  placeholder?: string;
  value?: string | number | boolean | Date;
  valueProperty?: unknown extends T ? string : keyof T;
  ignoreValue?: boolean;
  required?: boolean;
  disabled?: boolean;
  regex?: RegExp;
  // not used directly in FormBuilder but used to populate according to other options
  validators?: ValidatorFn | Array<ValidatorFn>;
  type: DynamicModelType;
  inputType?: IDynamicInputType;
  order?: number;
  options?: IDynamicModelOptions;
  selected?: boolean;
  isFormBuilder?: boolean;
  isReusable?: boolean;
  readOnly?: boolean;
  accessRoleIds?: string;
}
export type IDynamicUpdateOnValueChangeValidationTypes = 'maxDate' | 'minDate';
export interface IDynamicUpdateOnValueChange {
  formControlName?: string;
  property?: string;
  matchTargetProperty?: string;
  query?: string;
  // can enforce validation types on other controls based on value of the control defining this option
  validation?: IDynamicUpdateOnValueChangeValidationTypes;
  /** Can be used to calculate the value based on the selected value and other values in the form which can be then passed to matchTargetProperty of the formControlName.
   * Useful for example if CompanyDropDown changes calculate based on the CompanyId and facility location (other field in the form) the employee id and pass it to formControlName.matchTargetProperty */
  calculatedPropertyValue?: (
    selectedValue: unknown,
    config: IDynamicModel,
    group: UntypedFormGroup
  ) => Observable<string | number>;
}

/**
 * Per-control opt-in for conflict resolution when an auto-default (pushed via another control's
 * `updateOnValueChange`) would overwrite a manually-selected or saved ("sticky") value.
 * Declared on the *target* control's `options.conflictResolution`. Source controls need no config —
 * they self-register via their existing `updateOnValueChange` entries that target this control.
 */
export interface IDynamicConflictResolution {
  /**
   * 'prompt'         => show an icon + popover so the user picks keep vs. switch-to-default.
   * 'silent-keep'    => never overwrite a sticky value with an auto-default (no UI).
   * 'silent-default' => today's behavior: the auto-default always overwrites.
   * DEFAULT when the key is present but `mode` is omitted: 'prompt'.
   * (The whole `conflictResolution` key absent is treated as 'silent-default'.)
   */
  mode?: 'prompt' | 'silent-keep' | 'silent-default';
  /** prompt mode: true => red icon + invalidates the form until resolved; false (default) => yellow + form still saveable. */
  required?: boolean;
  /**
   * What to do when the recalculated default is null/empty while a sticky value is held
   * (the new source selection has no associated value):
   * - 'keep'   => keep the sticky value silently, no icon.
   * - 'clear'  => clear the sticky value silently (pre-feature behavior).
   * - 'prompt' (default) => raise a conflict offering "Keep <current>" vs "Clear (no default for this selection)".
   * Only consulted in `mode: 'prompt'`; under 'silent-keep' an empty default always keeps.
   */
  emptyDefault?: 'keep' | 'clear' | 'prompt';
  /** Friendly noun for the popover wording (e.g. 'Business Development contact'). Defaults to `config.label`. */
  valueLabel?: string;
  /**
   * Render a value object as text for the popover. Defaults to item?.Name ?? FullName ?? DisplayName ?? String(item).
   * Declared as a method (not a `(item) => string` property) so its parameter is checked bivariantly under
   * strictFunctionTypes — this lets callers narrow `item` to the control's value shape (e.g.
   * `(item: { Name?: string } | null) => ...`) and keep that type inside the body, instead of being forced to `unknown`.
   */
  displayValue?(item: unknown): string;
}

/** Resolved (defaults-applied) view of a control's conflict-resolution config. */
export interface IDynamicConflictState {
  targetFormControlName: string;
  targetLabel?: string;
  valueLabel?: string;
  required: boolean;
  status: 'unresolved' | 'resolved';
  /** the source control the user just changed that triggered this conflict */
  sourceFormControlName?: string;
  sourceLabel?: string;
  sourceValueDisplay?: string;
  /**
   * Labels of every source control that currently feeds this target's auto-default (e.g. Company +
   * Customer + Facility all drive a Business Development default). Lets the popover name the whole
   * combination — "CO# and Customer" — not just the one just changed. Includes only sources that
   * currently hold a value. Falls back to sourceLabel (the single trigger) when absent.
   */
  sourceLabels?: string[];
  /** suggested (auto-default) value text */
  defaultDisplay?: string;
  /** current/kept value text */
  currentDisplay?: string;
  /** true when the new source selection has NO default for this target (the "switch" option clears it). */
  defaultIsEmpty?: boolean;
}

/** Message broadcast on `DynamicRenderService.conflictUpdate$` (mirrors the `itemTargetUpdate$` bus). */
export interface IDynamicConflictBroadcast {
  /** which formControlName this message is addressed to */
  formControlName: string;
  /** the label's role for this conflict: it owns the value ('target') or it is one of the inputs ('source') */
  role: 'target' | 'source';
  type: 'raise' | 'resolve' | 'clear';
  /** the target whose conflict this is about — lets a source label that feeds several targets address the right one */
  targetFormControlName?: string;
  state?: IDynamicConflictState;
  /** on resolve */
  choice?: 'default' | 'keep';
}

// all top level properties of each dynamic model: useful for admin builder
export const dynamicModelBaseLevelKeys: Array<keyof IDynamicModel> = [
  'clientId',
  'formControlName',
  'formArrayName',
  'reportCategoryName',
  'label',
  'instruction',
  'placeholder',
  'value',
  'valueProperty',
  'ignoreValue',
  'required',
  'disabled',
  'type',
  'inputType',
  'order',
  'options',
  'selected',
  'isReusable',
  'readOnly',
  'accessRoleIds',
];
export const dynamicModelNonNullableKeys: Array<keyof IDynamicModel> = [
  'required',
  'isReusable',
];
// dynamic types which utlize formArrayName instead of formControlName
export const dynamicModelFormArrayTypes: Array<DynamicModelType> = [
  'accordion',
];
// admin builder properties can contain custom button actions
// collection of admin formControlNames used for 'button' types in admin
export const dynamicAdminButtonControlNameActions = [
  'AddItemToButtonGroup',
  'AddUpdateOnValueChange',
];

export const dynamicTypesUnsupportedOffline: Array<DynamicModelType> = ['file'];

export interface IDynamicModelProperties extends IDynamicModel {
  types?: Array<DynamicPropertyModelType>;
  // useful when needing to use a catch-all in 'types' while excluding particular ones
  // for example: 'typeahead-all' will match for all typeaheads, however you can exclude some if needed
  typesExclusions?: Array<DynamicPropertyModelType>;
  isAdvanced?: boolean;
}

export interface IDynamicButtonGroup {
  label?: string;
  value?: string | number;
  selected?: boolean;
  selectedColor?: string;
  dynamicControlLabel?: string;
}

export interface IDynamicDisplayDate {
  day: string | number;
  month: string | number;
  year: string | number;
  hour?: string | number;
  minute?: string | number;
  second?: string | number;
}

export interface IDynamicDatePicker {
  enable: boolean;
  isRoot?: boolean;
  date?: Date;
  fieldId?: string;
  formControlName?: string;
  selectedDate?: string | number | Date;
  showTime?: boolean;
  displayDate?: IDynamicDisplayDate;
  minDate?: Date;
}

export interface IDynamicDisplayTime {
  day: string | number;
  month: string | number;
  year: string | number;
  hour?: string | number;
  minute?: string | number;
  second?: string | number;
}

export interface IDynamicTimePicker {
  enable: boolean;
  isRoot?: boolean;
  time?: Date;
  fieldId?: string;
  formControlName?: string;
  selectedTime?: string | number | Date;
  displayTime?: IDynamicDisplayTime;
  minHour?: number;
  minMinute?: number;
}

// various settings that can be returned for component settings when registering new controls dynamically
export interface IDynamicRegisterControl {
  valueObjectId?: string | number | Date;
}

export interface IDynamicModelGroup {
  groupType: string;
  groupName: string;
  groupDescription?: string;
  controls: IDynamicModel[];
}

export interface IDynamicContainer {
  metadata?: IDynamicContainerMetadata;
  groups: IDynamicModelGroup[];
}

export interface IDynamicActiveGroupSettings {
  accordionApplyAll?: string /* formArrayName of accordion used to apply all */;
  accordionStatusComplete?: boolean /* whether the override has been found to validate */;
}

export interface IDynamicControlLayout {
  row?: number;
  col?: number;
  sizeX?: number;
  sizeY?: number;
  colSpan?: number;
}

export interface IDynamicTag {
  tagId?: string;
  tagName?: string;
  icon?: string;
  visible?: Array<string>;
}

export interface IDynamicTagDisplay {
  Id?: string;
  Name: string;
  tagName: string;
  icon?: string;
  visible?: string[];
}

export interface IDynamicContainerMetadata {
  dynamicContainerName?: string;
  dynamicContainerDescription?: string;
  originalVersionContainerId?: string;
  dynamicContainerWhatsNew?: string;
  displayIcon?: string;
  displayOrder?: { [key: string]: number };
  instructions?: string;
  isActive?: boolean;
  dynamicContainerId?: string;
  containerVersionId?: string;
  formId?: string;
  versionNumber?: number;
  createdDate?: string;
  modifiedDate?: string;
  modifiedBy?: string;
  createdBy?: string;
  isPublished?: boolean;
  tags?: Array<IDynamicTag>;
  isDirty?: boolean;
  isUsedForParticipation?: boolean;
  dynamicControls?: Array<IDynamicControl>;
  // (UI only) used for scoping drop down items when browsing all versions of same dynamicContainerId (helps provide a proper unique id)
  dynamicContainerIdAndVersion?: string;
  url?: string;
}

export interface IDynamicControl {
  createdBy?: string;
  createdDate?: string;
  dynamicControlId?: string;
  dynamicControlInstruction?: string;
  dynamicControlLabel?: string;
  dynamicControlName?: string;
  dynamicReportCategoryName?: string;
  dynamicControlType?: DynamicModelType;
  dynamicControls?: Array<IDynamicControl>;
  accessRoleIds?: string;
  inputType?: IDynamicInputType;
  isActive?: boolean;
  isPublished?: boolean;
  isReusable?: boolean;
  modifiedBy?: string;
  modifiedDate?: string;
  options: IDynamicModelOptions;
  order?: number;
  originalVersionControlId?: string;
  parentDynamicControlId?: string;
  placeholder?: string;
  reportCategoryName?: string;
  required?: boolean;
  validators?: ValidatorFn | Array<ValidatorFn>;
  value?: string | number | boolean;
  valueProperty?: string;
  versionNumber?: number;
  formArrayName?: string;
  disabled?: boolean;
  readOnly?: boolean;
  isFormBuilder?: boolean;
}

// Dynamic builder helpers
export interface IDynamicBuilderState {
  accordion?: { open?: { [key: string]: boolean } };
}

// Dynamic response (saving of user input from dynamic containers)
export interface IDynamicControlResponse {
  dynamicControlResponseId?: string;
  dynamicContainerResponseId?: string;
  dynamicControlId?: string;
  responseContent: string;
  responseContentJson?: string;
  isActive?: boolean;
  isFinal?: boolean;
  modifiedDate?: string;
  modifiedBy?: string;
  dynamicControlName?: string;
  dynamicControlType?: string;
}
/**
 * A typeahead control whose last remote search threw (hasSearchError_dynamic).
 * Attached at submit so the record can distinguish degraded empty vs real empty. ADO #30153.
 */
export interface ITypeaheadSearchFailure {
  formControlName: string;
  /** Human label when known from the dynamic model list. */
  label?: string;
}

export interface IDynamicResponseBody {
  dynamicContainerResponseId?: string;
  dynamicContainerId?: string;
  isActive?: boolean;
  isFinal?: boolean;
  isDraft?: boolean;
  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
  deleteComment?: string;
  dynamicControlResponses?: Array<IDynamicControlResponse>;
  submittedBy?: string;
  /**
   * Typeahead formControlNames that had an active remote-search failure at submit.
   * Client-authored from the FormGroup flag; backend may ignore until persisted. #30153.
   */
  typeaheadSearchFailures?: Array<ITypeaheadSearchFailure>;
}

export interface IDynamicSignaturePadOptions {
  penColor?: string;
  dotSize?: number;
  width?: number | string;
  height?: number | string;
  minWidth?: number;
  maxWidth?: number;
  hint?: string;
  showClearButton?: boolean;
  showDoneButton?: boolean;
}

export interface IDynamicPointGroup {
  color: string;
  points: IDynamicBasicPoint[];
}

export interface IDynamicBasicPoint {
  x: number;
  y: number;
  time: number;
}

export interface IDynamicReorderEvent {
  clientId: string;
  direction: 'up' | 'down';
}

export interface IDynamicPageOptions {
  name?: string;
  dynamicContainerId?: string;
  dynamicResponseId?: string;
  isCoaching?: boolean;
  isAICoaching?: boolean;
  isReview?: boolean;
  isEditReview?: boolean;
  showLastModified?: boolean;
  ignoreDefaultButton?: boolean;
  editDataItem?: unknown;
  reviewId?: string;
  deleteComment?: string;
  isDraft?: boolean;
  isReclassification?: boolean;
  showComments?: boolean;
  submittedBy?: string;
  submittedUserId?: string;
  isReadOnly?: boolean;
  shortName?: string;
  dynamicResponseBody?: IDynamicResponseBody;
}

export interface IDynamicActiveActionOptions {
  publish?: boolean;
  draft?: boolean;
  ignoreAutoSuccessMessage?: boolean;
}

export interface IFormReview {
  ReviewComment: string;
  FormReviewId?: string;
  FormId?: string;
  DynamicContainerId?: string;
  DynamicContainerResponseId?: string;
  FormReviewStatusId?: string;
  ReviewType: string;
  FormReviewStatusName?: string;
  Response?: string;
  ApprovalComments?: string;
  ReviewDate?: string;
  ReviewedBy?: string;
  ReviewedUserId?: string;
  bActive?: boolean;
  FormEmails: EmailQueueDto[];
  Likelihood?: number;
  Impact?: number;
  Value?: number;
  RecommendedForm?: string;
  ReclassifyReason?: string;
  RecommendReclassify?: boolean;
  LikelihoodReason?: string;
  ImpactReason?: string;
  ValueReason?: string;
  Feedback?: string;
  ReviewStatus?: string;
  // AI Coaching response properties
  likelihood?: number;
  impact?: number;
  value?: number;
  likelihoodReason?: string;
  impactReason?: string;
  valueReason?: string;
  reclassify?: string;
  reclassifyReason?: string;
  reviewStatus?: string;
  feedback?: string;
}

export interface ICannedResponse {
  CannedResponseId: string;
  Type: string;
  Response: string;
  clicked?: boolean;
}

// Note: mixed upper/camel - camel is what we create - the upper comes from backend right now (hopefully in future all backend apis will be standardized to not have capital casing)
export interface IDynamicOfflineCache {
  cacheDate?: number;
  Clients?: Array<unknown>;
  Companies?: Array<unknown>;
  Employees?: Array<unknown>;
  Facilities?: Array<unknown>;
  Jobs?: Array<unknown>;
  LessonLearnedCategories?: Array<unknown>;
  NearMissCategories?: Array<unknown>;
  data?: Array<unknown>;
}

export interface IDynamicCategory {
  icon: string;
  title: string;
  visible: string[];
}
